import React, { useMemo, useState } from 'react';
import type { Insight } from '../lib/insights';
import type { PillarResult } from '../lib/pillarScoring';
import type { DailyMetrics } from '../types';
import { localDateString } from '../lib/dashboardHelpers';

interface Props {
  insights: Insight[];
  pillarResults: Record<string, PillarResult>;
  pillarDeltas: Record<string, number>;
  metrics: DailyMetrics[];
  /**
   * Compact visual mode — tighter padding, smaller header, smaller bullet
   * rows. Used when this block is demoted below the primary insight to
   * reduce top-of-page competition.
   */
  compact?: boolean;
}

const PILLAR_LABELS: Record<string, string> = {
  sleep: 'Sleep', movement: 'Movement', nutrition: 'Nutrition',
  stress: 'Stress', social: 'Social', substance: 'Substances',
};

interface Bullet {
  id: string;
  text: string;
  color: string;
}

function computeBullets(
  insights: Insight[],
  pillarResults: Record<string, PillarResult>,
  pillarDeltas: Record<string, number>,
  metrics: DailyMetrics[],
): Bullet[] {
  const bullets: Bullet[] = [];
  const todayStr = localDateString();

  // 1. Top pillar change
  let bestKey = '', bestDelta = -Infinity, worstKey = '', worstDelta = Infinity;
  for (const [key, d] of Object.entries(pillarDeltas)) {
    if (d > bestDelta && PILLAR_LABELS[key]) { bestKey = key; bestDelta = d; }
    if (d < worstDelta && PILLAR_LABELS[key]) { worstKey = key; worstDelta = d; }
  }
  if (bestDelta > 3 && bestKey) {
    bullets.push({
      id: 'pillar-up',
      text: `${PILLAR_LABELS[bestKey]} improved by ${Math.round(bestDelta)} points`,
      color: '#22C55E',
    });
  } else if (worstDelta < -3 && worstKey) {
    bullets.push({
      id: 'pillar-down',
      text: `${PILLAR_LABELS[worstKey]} dropped ${Math.abs(Math.round(worstDelta))} points`,
      color: '#F59E0B',
    });
  }

  // 2. Top insight
  if (insights.length > 0) {
    const top = insights[0];
    bullets.push({
      id: `insight-${top.id}`,
      text: `${top.title} (${top.value})`,
      color: top.direction === 'positive' ? '#22C55E' : top.direction === 'negative' ? '#F59E0B' : '#94A3B8',
    });
  }

  // 3. Data coverage
  const recentDays = metrics.filter(d => d.date < todayStr).slice(-7);
  const activePillars = Object.values(pillarResults).filter(r => r.score != null).length;
  bullets.push({
    id: 'coverage',
    text: `Based on ${recentDays.length} days of data across ${activePillars} health pillars`,
    color: '#94A3B8',
  });

  return bullets;
}

export default React.memo(function WeeklyDigest({ insights, pillarResults, pillarDeltas, metrics, compact = false }: Props) {
  const todayStr = localDateString();
  const hasEnough = metrics.filter(d => d.date < todayStr).length >= 7;
  const [dismissed, setDismissed] = useState(false);

  const bullets = useMemo(
    () => computeBullets(insights, pillarResults, pillarDeltas, metrics),
    [insights, pillarResults, pillarDeltas, metrics],
  );

  if (!hasEnough || bullets.length < 2 || dismissed) return null;

  // Monday highlight — subtle accent border (same left-border pattern used by TodaysPrimaryInsight)
  const isStartOfWeek = new Date().getDay() === 1;

  // Compact overrides the big .card padding (p-9 from index.css) with
  // a tighter p-5 to reduce vertical footprint when this block is
  // demoted below the Primary Insight.
  const cardClass = compact ? 'card !p-5 !rounded-2xl' : 'card';

  return (
    <div
      className={cardClass}
      style={isStartOfWeek ? { borderLeft: '3px solid #6F8A5A' } : undefined}
    >
      <div className={`flex items-center justify-between ${compact ? 'mb-2' : 'mb-3'}`}>
        <div className="flex items-center gap-2">
          <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="text-arete-moss">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          <h2 className="section-label">Weekly Summary</h2>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="text-arete-muted hover:text-arete-navy dark:hover:text-gray-200 text-xs transition-colors"
          aria-label="Dismiss weekly summary"
        >
          &times;
        </button>
      </div>

      <div className={`flex flex-col ${compact ? 'gap-1.5' : 'gap-2.5'}`}>
        {bullets.map(b => (
          <div key={b.id} className={`flex items-start ${compact ? 'gap-2' : 'gap-2.5'}`}>
            <div
              className={`${compact ? 'w-1.5 h-1.5 mt-1.5' : 'w-2 h-2 mt-1'} rounded-full flex-shrink-0`}
              style={{ backgroundColor: b.color }}
            />
            <p className={`${compact ? 'text-xs' : 'text-sm'} text-arete-navy dark:text-gray-200 leading-snug`}>{b.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
});
